import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  fetchOrgInvitations,
  inviteUserByPhone,
  revokeOrgInvitation,
  type OrgInvitation,
} from "@/lib/api";
import { inviteSmsReasonText, maskPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { OrgRoleBadge } from "@/components/OrgRoleBadge";
import { Clock, Send, X } from "@/components/icons";
import { useT, type Translate } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";
import { viewerInstant } from "@/app/displayFormat";

/**
 * Pending phone invitations for one organization.
 *
 * An invitation is recorded whether or not it can be texted (the SMS channel and
 * self-registration are separate kill switches), so this section lists what is
 * outstanding and, when the channel is off, surfaces the registration link for
 * the operator to pass on by hand.
 *
 * Authorization stays with the backend: every button calls an org-scoped endpoint
 * that re-checks the caller. `isAdmin` only decides what is worth rendering — a
 * plain org manager may not revoke a manager invitation (the server answers 403),
 * so that button is not shown to them.
 */
export function PendingInvitations({
  orgId,
  canManage,
  isAdmin,
}: {
  orgId: number;
  /** Whether the current user manages this org at all. */
  canManage: boolean;
  /** System admin — may act on manager-role invitations too. */
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const t = useT();

  const invitesQ = useQuery({
    queryKey: ["org-invitations", orgId],
    queryFn: () => fetchOrgInvitations(orgId),
    enabled: canManage,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["org-invitations", orgId] });

  const revokeM = useMutation({
    mutationFn: (invitationId: number) => revokeOrgInvitation(orgId, invitationId),
    onSuccess: () => {
      toast.success(t("operator.auth.invitation_revoked"));
      refresh();
    },
    onError: (e) => toast.error(errorMessage(t, e, { family: "org_invitation_revoke", fallback: "operator.auth.invitation_revoke_failed" })),
  });

  // Resend = re-POST the same phone + role. The backend reuses the existing
  // pending row (idempotent per org+phone) and texts it again, so this can never
  // create a duplicate invitation.
  const resendM = useMutation({
    mutationFn: (inv: OrgInvitation) => inviteUserByPhone(orgId, inv.phone, inv.role),
    onSuccess: (res) => {
      if (res.sms_sent) toast.success(t("operator.auth.invitation_resent"));
      else toast.warning(inviteSmsReasonText(res.sms_reason, t));
      refresh();
    },
    onError: (e) => toast.error(errorMessage(t, e, { family: "org_invitation_create", fallback: "operator.auth.invitation_resend_failed" })),
  });

  if (!canManage) return null;

  const invitations = invitesQ.data?.invitations ?? [];
  const smsChannel = invitesQ.data?.sms_channel;
  const registrationLink = invitesQ.data?.registration_link;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm">{t("operator.auth.invitations_title", { count: invitations.length })}</h4>
      </div>

      {/* An invitation that cannot be texted is not broken; it just needs the
          link delivered another way. */}
      {smsChannel && !smsChannel.available && (
        <div className="mb-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
          <div>{inviteSmsReasonText(smsChannel.reason, t)}</div>
          {registrationLink && (
            <div className="mt-1 opacity-70">
              {t("operator.auth.invitations_manual_link")}{" "}
              <span className="font-mono">{registrationLink}</span>
            </div>
          )}
        </div>
      )}

      {invitesQ.isLoading ? (
        <div className="text-sm opacity-50 italic">{t("common.state.loading")}</div>
      ) : invitations.length === 0 ? (
        <div className="text-sm opacity-50 italic">{t("operator.auth.invitations_empty")}</div>
      ) : (
        <div className="space-y-1">
          {invitations.map((inv) => {
            // Mirrors the server rule: only a system admin may act on a pending
            // manager invitation.
            const mayAct = isAdmin || inv.role === "driver";
            return (
              <div
                key={inv.id}
                className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-muted/50 text-sm"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Clock className="h-3.5 w-3.5 shrink-0 opacity-50" />
                  <span className="font-mono">{maskPhone(inv.phone)}</span>
                  <OrgRoleBadge role={inv.role} />
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs opacity-60">{formatInvitedAt(t, inv)}</span>
                  {mayAct && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2"
                        title={t("operator.auth.invitation_resend")}
                        disabled={resendM.isPending}
                        onClick={() => resendM.mutate(inv)}
                      >
                        <Send className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-destructive hover:text-destructive"
                        title={t("operator.auth.invitation_revoke")}
                        disabled={revokeM.isPending}
                        onClick={() => {
                          if (confirm(t("operator.auth.invitation_revoke_confirm", { phone: maskPhone(inv.phone) })))
                            revokeM.mutate(inv.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** "Invited Aug 12, 2026 by admin" — the inviter falls back to their id, then to
 *  nothing, so a deleted account never blanks out the date too. */
function formatInvitedAt(t: Translate, inv: OrgInvitation): string {
  const when = viewerInstant(inv.created_at, "date") ?? "";
  const who =
    inv.invited_by_username ??
    (inv.invited_by_user_id != null ? t("operator.auth.invitation_inviter_id", { id: String(inv.invited_by_user_id) }) : null);
  const form = when && who ? "dated_by" : when ? "dated" : who ? "by" : "none";
  return t("operator.auth.invitation_invited", { form, when, who: who ?? "" });
}
